import { Link } from "react-router-dom";
import {
  Sparkles,
  Target,
  Eye,
  ShieldCheck,
  Handshake,
  Lightbulb,
  Users,
  Glasses,
  MapPin,
} from "lucide-react";
import useSeo from "../hooks/useSeo";
import useCmsContent from "../hooks/useCmsContent";

const ABOUT_DEFAULTS = {
  title: "Sobre RivoNes",
  lead: "La plataforma mexicana de renta de vehículos entre particulares — desarrollada por Novus Core Technologies LLC.",
};

const SECTIONS = [
  {
    id: "sobre-nosotros",
    n: "1",
    icon: Sparkles,
    title: "Sobre Nosotros",
    body: (
      <>
        <p>
          Bienvenido a <strong>RivoNes</strong>, una plataforma moderna de renta de
          vehículos entre particulares diseñada para transformar la manera en que las
          personas rentan y comparten automóviles en México.
        </p>
        <p>
          En RivoNes conectamos a propietarios de vehículos con usuarios que buscan una
          alternativa más rápida, accesible y conveniente que las arrendadoras
          tradicionales. Nuestra plataforma permite realizar todo el proceso de forma
          digital: desde la búsqueda y reservación del vehículo, hasta la verificación
          de identidad, pagos y comunicación entre usuarios y anfitriones.
        </p>
        <p>
          Nacimos con la visión de simplificar la movilidad en México mediante
          tecnología, seguridad y confianza. Creemos que rentar un automóvil debe ser un
          proceso sencillo, transparente y sin complicaciones. Por eso, desarrollamos
          una experiencia intuitiva enfocada tanto en viajeros como en personas locales
          que necesitan movilidad temporal de manera segura y eficiente.
        </p>
        <p>
          RivoNes busca impulsar la <strong>economía colaborativa</strong> permitiendo
          que los propietarios generen ingresos adicionales con sus vehículos, mientras
          los usuarios acceden a opciones de transporte más flexibles y competitivas.
        </p>
        <p>
          Nuestra plataforma incorpora herramientas de verificación, sistemas de
          calificación, protección de cuentas y procesos digitales diseñados para crear
          una comunidad confiable y profesional.
        </p>
        <div className="mt-5 p-5 rounded-2xl border border-terracotta/30 bg-terracotta/5 not-prose flex items-start gap-3">
          <MapPin className="w-5 h-5 text-terracotta mt-0.5 flex-shrink-0" />
          <p className="text-sm text-neutral-700 m-0">
            Actualmente iniciamos operaciones en <strong>Tijuana, Baja California</strong>,
            con el objetivo de expandirnos a las principales ciudades de México y
            posteriormente a otros mercados de Latinoamérica.
          </p>
        </div>
      </>
    ),
  },
  {
    id: "mision",
    n: "2",
    icon: Target,
    title: "Nuestra Misión",
    body: (
      <>
        <p>
          Nuestra misión es <strong>revolucionar la movilidad en México</strong>{" "}
          mediante una plataforma tecnológica segura, moderna y accesible que conecte a
          propietarios de vehículos con personas que necesitan transportación confiable.
        </p>
        <p>
          Buscamos ofrecer una experiencia simple, transparente y eficiente, generando
          oportunidades económicas para nuestros anfitriones y brindando a nuestros
          usuarios una alternativa práctica a las rentadoras tradicionales.
        </p>
        <p>
          En RivoNes trabajamos para construir una comunidad basada en la confianza, la
          innovación y la libertad de movilidad.
        </p>
      </>
    ),
  },
  {
    id: "vision",
    n: "3",
    icon: Eye,
    title: "Nuestra Visión",
    body: (
      <>
        <p>
          Convertirnos en la <strong>plataforma líder de renta de vehículos entre
          particulares</strong> en México y Latinoamérica, reconocida por su innovación
          tecnológica, seguridad y excelencia en experiencia de usuario.
        </p>
        <p>
          Aspiramos a redefinir la forma en que las personas acceden al transporte,
          creando una red de movilidad más accesible, eficiente y conectada para todos.
        </p>
      </>
    ),
  },
  {
    id: "valores",
    n: "4",
    icon: Handshake,
    title: "Nuestros Valores",
    body: (
      <>
        <p>Los principios que guían cada decisión que tomamos en RivoNes:</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 not-prose mt-4">
          <ValueCard
            icon={ShieldCheck}
            title="Seguridad"
            desc="Priorizamos la protección de nuestros usuarios mediante procesos de verificación, tecnología y medidas de seguridad avanzadas."
          />
          <ValueCard
            icon={Handshake}
            title="Confianza"
            desc="Fomentamos relaciones transparentes entre anfitriones y usuarios a través de perfiles verificados, calificaciones y comunicación clara."
          />
          <ValueCard
            icon={Lightbulb}
            title="Innovación"
            desc="Utilizamos tecnología moderna para simplificar y mejorar continuamente la experiencia de renta de vehículos."
          />
          <ValueCard
            icon={Users}
            title="Comunidad"
            desc="Creemos en el poder de la economía colaborativa para generar oportunidades y beneficiar a miles de personas."
          />
          <ValueCard
            icon={Glasses}
            title="Transparencia"
            desc="Trabajamos con claridad en nuestros procesos, políticas y servicios para brindar tranquilidad y confianza a nuestra comunidad."
            className="sm:col-span-2"
          />
        </div>
      </>
    ),
  },
];

function ValueCard({ icon: Icon, title, desc, className = "" }) {
  return (
    <div
      className={`p-5 rounded-2xl border border-neutral-200 bg-white hover:border-terracotta/40 hover:shadow-sm transition-all ${className}`}
      data-testid={`valor-${title.toLowerCase()}`}
    >
      <div className="flex items-center gap-3 mb-2">
        <span className="inline-flex w-10 h-10 rounded-xl bg-terracotta/10 items-center justify-center">
          <Icon className="w-5 h-5 text-terracotta" strokeWidth={2} />
        </span>
        <h4 className="font-heading font-semibold text-lg text-[#111111] m-0">{title}</h4>
      </div>
      <p className="text-sm text-neutral-600 leading-relaxed m-0">{desc}</p>
    </div>
  );
}

export default function SobreNosotrosPage() {
  const { content: cms } = useCmsContent("about", ABOUT_DEFAULTS);
  useSeo({
    title: "Sobre Nosotros · Rivones",
    description:
      "Conoce a RivoNes — la plataforma mexicana de renta de vehículos entre particulares. Nuestra misión, visión y los valores que nos guían.",
    keywords:
      "sobre nosotros rivones, misión rivones, visión rivones, valores rivones, renta de autos México",
    canonical: "https://rivones.com/sobre-nosotros",
  });

  return (
    <div className="bg-sand min-h-screen" data-testid="sobre-nosotros-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-neutral-50 to-sand border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <p className="text-sm font-semibold tracking-[0.2em] text-terracotta uppercase">
            Nuestra historia
          </p>
          <h1
            className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-[#111111] tracking-tight mt-3"
            data-testid="sobre-nosotros-h1"
          >
            {cms.title}
          </h1>
          <p className="text-base sm:text-lg text-neutral-600 mt-4 leading-relaxed">
            {cms.lead}
          </p>
          <p className="text-xs uppercase tracking-[0.18em] text-neutral-400 mt-6 font-semibold">
            Última actualización · Febrero 2026
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* TOC */}
        <nav
          aria-label="Índice"
          className="bg-white border border-neutral-200 rounded-2xl p-5 mb-10"
          data-testid="sobre-nosotros-toc"
        >
          <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-3">
            Índice
          </p>
          <ol className="space-y-1.5 text-sm">
            {SECTIONS.map(({ id, n, title }) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  className="text-neutral-700 hover:text-terracotta transition-colors flex gap-2"
                >
                  <span className="text-neutral-400 tabular-nums">{n}.</span>
                  <span>{title}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-12">
          {SECTIONS.map(({ id, n, icon: Icon, title, body }) => (
            <article
              key={id}
              id={id}
              data-testid={`sobre-nosotros-section-${id}`}
              className="scroll-mt-24"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex w-11 h-11 rounded-xl bg-neutral-100 items-center justify-center">
                  <Icon className="w-5 h-5 text-[#111111]" strokeWidth={2} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-neutral-400 font-semibold">
                    Sección {n}
                  </p>
                  <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#111111] leading-tight">
                    {title}
                  </h2>
                </div>
              </div>
              <div className="max-w-none text-neutral-700 leading-relaxed [&_p]:my-3 [&_strong]:text-[#111111] [&_strong]:font-semibold [&_h4]:font-semibold [&_h4]:text-base">
                {body}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-14 p-5 rounded-2xl bg-neutral-100 border border-neutral-200 text-sm text-neutral-600">
          ¿Quieres saber cómo funciona la plataforma? Visita{" "}
          <Link to="/como-funciona" className="text-terracotta font-semibold hover:underline">
            Cómo funciona
          </Link>
          {" "}o consulta nuestros{" "}
          <Link to="/terminos" className="text-terracotta font-semibold hover:underline">
            Términos y Condiciones
          </Link>
          .
        </div>
      </section>
    </div>
  );
}
